import React, { ReactNode, CSSProperties } from 'react';
import { Typography } from '@ethings/ui-toolkit';
import { useResponsiveText, ResponsiveTextOptions } from '../../hooks/use-responsive-text';

/**
 * Typography variant types
 */
type TypographyVariant =
  | 'h1' | 'h2' | 'h3' | 'h4' | 'h5' | 'h6'
  | 'subtitle1' | 'subtitle2'
  | 'body1' | 'body2'
  | 'caption' | 'button' | 'overline';

/**
 * Props for ResponsiveText component
 */
export interface ResponsiveTextProps {
  /** Content to display */
  children: ReactNode;
  /** Typography variant (default: 'body1') */
  variant?: TypographyVariant;
  /** Maximum number of lines to display (default: 1) */
  maxLines?: number;
  /** Enable text wrapping on mobile (default: true) */
  enableWrap?: boolean;
  /** Options for responsive text behavior */
  responsiveOptions?: ResponsiveTextOptions;
  /** Additional CSS class name */
  className?: string;
  /** Inline styles */
  style?: CSSProperties;
  /** Data test ID for testing */
  'data-testid'?: string;
  /** Text alignment */
  align?: 'left' | 'center' | 'right' | 'justify' | 'inherit';
  /** Text color */
  color?: string;
  /** No wrap */
  noWrap?: boolean;
  /** Component to use (default: determined by variant) */
  component?: React.ElementType;
}

/**
 * Extract text content from ReactNode for length calculation
 */
const extractTextContent = (node: ReactNode): string => {
  if (typeof node === 'string' || typeof node === 'number') {
    return String(node);
  }

  if (Array.isArray(node)) {
    return node.map(extractTextContent).join('');
  }

  if (React.isValidElement(node)) {
    const props = node.props as { children?: ReactNode };
    if (props.children) {
      return extractTextContent(props.children);
    }
  }

  return '';
};

/**
 * Map Material-UI Typography variants to base font sizes
 */
const variantToFontSize: Record<string, string> = {
  h1: '2.5rem',
  h2: '2.125rem',
  h3: '1.75rem',
  h4: '1.5rem',
  h5: '1.25rem',
  h6: '1.125rem',
  subtitle1: '1rem',
  subtitle2: '0.875rem',
  body1: '1rem',
  body2: '0.875rem',
  caption: '0.75rem',
  button: '0.875rem',
  overline: '0.75rem',
};

/**
 * A responsive text component that automatically adjusts font size based on
 * screen size and text length. Especially useful for handling longer text in
 * languages like Spanish, German, French, etc.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <ResponsiveText variant="h5">
 *   Some title text
 * </ResponsiveText>
 *
 * // With multiline support
 * <ResponsiveText variant="body1" maxLines={2} enableWrap>
 *   Longer text that may wrap to multiple lines on mobile
 * </ResponsiveText>
 *
 * // With custom responsive options
 * <ResponsiveText
 *   variant="subtitle1"
 *   responsiveOptions={{
 *     minFontSize: '0.8rem',
 *     maxFontSize: '1rem',
 *     textLengthThreshold: 30
 *   }}
 * >
 *   Custom configured text
 * </ResponsiveText>
 * ```
 */
export const ResponsiveText: React.FC<ResponsiveTextProps> = ({
  children,
  variant = 'body1',
  maxLines = 1,
  enableWrap = true,
  responsiveOptions,
  style,
  className,
  align,
  color,
  noWrap,
  component,
  'data-testid': dataTestId,
}) => {
  const textContent = extractTextContent(children);
  const textLength = textContent.length;

  const baseSize = variantToFontSize[variant] || '1rem';

  const responsiveStyles = useResponsiveText(baseSize, textLength, responsiveOptions);

  const combinedStyle: React.CSSProperties = {
    fontSize: responsiveStyles.fontSize,
    lineHeight: responsiveStyles.lineHeight,
    ...(enableWrap
      ? {
        wordBreak: responsiveStyles.wordBreak,
        whiteSpace: maxLines > 1 ? 'normal' : responsiveStyles.whiteSpace,
        overflow: responsiveStyles.overflow,
        textOverflow: responsiveStyles.textOverflow,
      }
      : {
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }),
    ...(maxLines > 1 && {
      display: '-webkit-box',
      WebkitLineClamp: maxLines,
      WebkitBoxOrient: 'vertical',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    } as React.CSSProperties),
    ...style,
  };

  return (
    <Typography
      variant={variant}
      style={combinedStyle}
      className={className}
      align={align}
      color={color}
      noWrap={noWrap}
      component={component}
      data-testid={dataTestId}
    >
      {children}
    </Typography>
  );
};

ResponsiveText.displayName = 'ResponsiveText';
